import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLocation, useNavigate } from 'react-router-dom';
import { Expand, Share, Download, Copy, Upload, Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/use-toast';
import QRCode from 'qrcode';
import { createWorker } from 'tesseract.js';

const WalletDepositPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(location.state?.amount || '100');
  const [utrNumber, setUtrNumber] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [error, setError] = useState('');
  const [extractingUtr, setExtractingUtr] = useState(false);
  const [utrExtractError, setUtrExtractError] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(null);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  // Fetch payment options on component mount
  useEffect(() => {
    fetchPaymentOptions();
  }, []);

  // Generate QR code when amount or selected payment option changes
  useEffect(() => {
    if (selectedOption && paymentOptions.length > 0) {
      const current = paymentOptions.find(option => option.id === selectedOption);
      if (current && current.upiId) {
        generateQRCode(current.upiId, amount);
      } else {
        setQrCodeDataUrl(''); // Clear stale QR when method has no UPI ID
      }
    }
  }, [amount, selectedOption, paymentOptions]);

  const fetchPaymentOptions = async () => {
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('reddyannabook_token');
      if (!token) {
        setError('Please log in to continue.');
        setLoading(false);
        navigate('/login');
        return;
      }

      let response;
      try {
        response = await fetch(API_ENDPOINTS.WALLET_PAYMENT_OPTIONS, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (networkErr) {
        setError('Could not reach server. Check your connection and that the backend is running.');
        toast({
          title: 'Network error',
          description: 'Could not reach server. Please try again.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (_) {
        setError('Invalid response from server. Please try again.');
        setLoading(false);
        return;
      }

      if (response.status === 401) {
        setError('Your session has expired. Please log in again.');
        toast({
          title: 'Session expired',
          description: 'Please log in again to continue.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      if (response.status === 429) {
        setError('Too many requests. Please wait a few minutes and try again.');
        toast({
          title: 'Rate limit',
          description: 'Please wait a few minutes before trying again.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      if (!response.ok) {
        setError(data?.message || 'Failed to fetch payment options. Please try again.');
        setLoading(false);
        return;
      }

      if (data?.success && data?.data?.payment_options?.length > 0) {
        setPaymentOptions(data.data.payment_options);
        setSelectedOption(data.data.payment_options[0].id);
      } else {
        setError('No payment options available. Please contact support.');
      }
    } catch (err) {
      setError(err.message || 'Failed to load payment options. Please try again.');
      toast({
        title: 'Error',
        description: err.message || 'Failed to load payment options.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = async () => {
    // Check for existing pending deposit first
    try {
      const token = localStorage.getItem('reddyannabook_token');
      if (token) {
        const checkRes = await fetch(API_ENDPOINTS.WALLET_CHECK_PENDING, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const checkData = await checkRes.json();
        if (checkData.data?.has_pending_deposit) {
          setShowPendingModal({ type: 'deposit', message: checkData.data.message });
          return;
        }
      }
    } catch {}

    if (!utrNumber || utrNumber.trim().length < 10) {
      toast({
        title: "Invalid UTR",
        description: "UTR / Transaction ID must be at least 10 characters",
        variant: "destructive",
      });
      return;
    }

    if (!receiptFile) {
      toast({
        title: "Receipt Required",
        description: "Please upload your payment screenshot",
        variant: "destructive",
      });
      return;
    }

    if (!agreeTerms) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms & conditions",
        variant: "destructive",
      });
      return;
    }

    if (!selectedOption) {
      toast({
        title: "Validation Error",
        description: "Please select a payment option",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('reddyannabook_token');
      if (!token) {
        navigate('/login');
        return;
      }


      // Compress and convert image to base64
      const base64String = await compressImage(receiptFile);

      const selectedPaymentOption = paymentOptions.find(opt => opt.id === selectedOption);

      const requestData = {
        amount: parseFloat(amount),
        payment_option_id: selectedPaymentOption.id,
        utr_number: utrNumber,
        receipt_file: base64String
      };

      const response = await fetch(API_ENDPOINTS.WALLET_SUBMIT_DEPOSIT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      const rawText = await response.text();
      let data;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch (_) {
        throw new Error(
          response.ok
            ? 'Invalid response from server. Please try again.'
            : 'Server error. Please try again or contact support.'
        );
      }

      if (!response.ok) {
        throw new Error(data?.message || 'Failed to submit deposit request');
      }

      if (data && data.success) {
        toast({
          title: "Success",
          description: data.data.message || "Deposit request submitted successfully!",
        });
        navigate('/wallet');
      } else {
        throw new Error(data?.message || 'Failed to submit deposit request');
      }
    } catch (err) {
      console.error('Deposit submit error:', err);
      const msg = err.message || 'Failed to submit deposit request. Please try again.';
      setError(msg);
      toast({
        title: "Deposit Failed",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Compress image to max 1200px width, JPEG quality 0.7
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1200;
        const ratio = Math.min(maxW / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Return base64 WITHOUT the data:image/jpeg;base64, prefix
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        resolve(dataUrl.split(',')[1]);
      };
      img.onerror = () => {
        // Fallback: read original as base64
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(file);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Text copied to clipboard",
    });
  };

  // Get icon URL with fallback to default icons
  const getIconUrl = (option) => {
    if (option.iconUrl) {
      return option.iconUrl;
    }
    
    // Default icons based on payment type
    if (option.type === 'upi') {
      return 'https://logodix.com/logo/1763566.png';
    } else if (option.type === 'netbanking') {
      return 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b1/IMPS_logo.svg/2560px-IMPS_logo.svg.png';
    }
    
    return null;
  };

  const downloadQRCode = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `qr-code-${selectedOption}-${amount}.png`;
      link.href = qrCodeDataUrl;
      link.click();
      toast({
        title: "Downloaded!",
        description: "QR code downloaded successfully",
      });
    }
  };

  // Generate QR code with safety watermark (warning overlay drawn on canvas)
  const generateQRCode = async (upiId, amount) => {
    try {
      let qrData;
      if (parseFloat(amount) <= 2000 && parseFloat(amount) > 0) {
        qrData = `upi://pay?pa=${upiId}&am=${amount}&cu=INR&tn=Deposit`;
      } else {
        qrData = `upi://pay?pa=${upiId}&cu=INR&tn=Deposit`;
      }

      const dataUrl = await QRCode.toDataURL(qrData, {
        width: 400,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: { dark: '#000000', light: '#ffffff' },
      });

      // Draw watermark via Canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = dataUrl;
      });

      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const lines = [
        "DON'T PAY ANY AMOUNT",
        'FOR INVESTMENT OR',
        'MONEY DOUBLING OR',
        'PURCHASE ONLINE GOODS',
      ];
      const w = canvas.width;
      const h = canvas.height;
      const fontSize = Math.floor(w * 0.052);
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const lineHeight = fontSize * 1.35;
      const totalTextH = lines.length * lineHeight;
      const startY = h * 0.52 - totalTextH / 2 + lineHeight / 2;

      lines.forEach((line, i) => {
        const y = startY + i * lineHeight;
        // White halo for contrast
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        [[-2, -2], [-2, 2], [2, -2], [2, 2], [-2, 0], [2, 0], [0, -2], [0, 2]].forEach(
          ([ox, oy]) => ctx.fillText(line, w / 2 + ox, y + oy)
        );
        // Red warning text
        ctx.fillStyle = 'rgba(220,30,30,0.65)';
        ctx.fillText(line, w / 2, y);
      });

      setQrCodeDataUrl(canvas.toDataURL('image/png'));
    } catch (error) {
      console.error('Error generating QR code:', error);
      setQrCodeDataUrl('');
    }
  };

  /** Parse OCR text for Transaction ID, UTR, or Reference number. Handles OCR noise (0/O, 1/I). */
  const parseUtrFromText = (text) => {
    if (!text || !text.trim()) return null;
    // Normalize common OCR confusions for "Transaction ID"
    const normalized = text
      .replace(/\r\n/g, '\n')
      .replace(/\s+/g, ' ');
    const patterns = [
      /Transaction\s*I[Dd0]\s*:?\s*(\d{8,})/i,
      /Transaction\s*ID\s*:?\s*(\d+)/i,
      /UTR\s*(?:Number)?\s*:?\s*([A-Za-z0-9]{8,})/i,
      /(?:Ref\.?\s*No\.?|Reference)\s*:?\s*([A-Za-z0-9]{8,})/i,
    ];
    for (const re of patterns) {
      const m = normalized.match(re);
      if (m && m[1]) return m[1].trim();
    }
    // Fallback: longest sequence of 10+ digits (common for UTR/Transaction ID)
    const digitBlocks = text.match(/\d{10,}/g);
    if (digitBlocks && digitBlocks.length > 0) {
      const best = digitBlocks.sort((a, b) => b.length - a.length)[0];
      return best;
    }
    return null;
  };

  const extractUtrFromImage = async (file) => {
    setExtractingUtr(true);
    setUtrExtractError(false);
    let worker;
    let timedOut = false;

    // 8-second timeout — if OCR takes too long, let user type manually
    const timeout = setTimeout(() => {
      timedOut = true;
      setExtractingUtr(false);
      setUtrExtractError(true);
      if (worker) try { worker.terminate(); } catch (_) {}
      toast({
        title: 'OCR timed out',
        description: 'Taking too long. Please type the UTR number manually.',
        variant: 'destructive',
      });
    }, 8000);

    try {
      worker = await createWorker('eng', 1, {
        workerPath: 'https://cdn.jsdelivr.net/npm/tesseract.js@7/dist/worker.min.js',
      });
      if (timedOut) { try { await worker.terminate(); } catch (_) {} return; }
      const { data: { text } } = await worker.recognize(file);
      clearTimeout(timeout);
      await worker.terminate();
      worker = null;
      if (timedOut) return;
      const utr = parseUtrFromText(text);
      if (utr) {
        setUtrNumber(utr);
      } else {
        setUtrExtractError(true);
        toast({
          title: 'UTR not detected',
          description: 'Could not find UTR/Transaction ID in the image. Please enter it manually.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      clearTimeout(timeout);
      if (timedOut) return;
      console.warn('OCR failed:', err);
      if (worker) try { await worker.terminate(); } catch (_) {}
      setUtrExtractError(true);
      toast({
        title: 'Image read failed',
        description: 'Could not read the image. Please enter the UTR number manually.',
        variant: 'destructive',
      });
    } finally {
      clearTimeout(timeout);
      if (!timedOut) setExtractingUtr(false);
    }
  };

  const onSelectFile = (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only image files allowed (JPG, PNG, GIF, WEBP). PDF and other files not accepted.');
      return;
    }
    setReceiptFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    extractUtrFromImage(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    onSelectFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    onSelectFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const clearReceipt = () => {
    setReceiptFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setUtrNumber('');
    setUtrExtractError(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const current = paymentOptions.find(p => p.id === selectedOption);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-600" />
          <p className="text-gray-600">Loading payment options...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isSessionExpired = /session|expired|log in/i.test(error);
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6">
        <div className="max-w-md mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700 mb-4">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {isSessionExpired ? (
                <Button onClick={() => navigate('/login')} className="bg-red-600 hover:bg-red-700">
                  Log in again
                </Button>
              ) : (
                <Button onClick={fetchPaymentOptions} className="bg-red-600 hover:bg-red-700">
                  Retry
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 px-4 py-6 pb-[120rem]">
      <div className="max-w-md mx-auto space-y-6 relative z-0">
        {/* Amount confirmation notice */}
        {location.state?.amount && (
          <div className="bg-green-50 border-2 border-green-400 rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">💰</span>
              <div>
                <p className="font-bold text-green-800 text-lg">Please send ₹{Number(location.state.amount).toLocaleString('en-IN')}</p>
                <p className="text-sm text-green-600">Send exact amount to the payment method below</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 mt-2 border border-green-200">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">Amount to send:</span>
                <span className="text-green-700 font-bold text-xl">₹{Number(location.state.amount).toLocaleString('en-IN')}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Send exact amount — different amount may delay credit</p>
            </div>
          </div>
        )}
        {/* Payment Options */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Payment Options</h2>
          <div className="grid grid-cols-2 gap-3">
            {paymentOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setSelectedOption(opt.id)}
                className={`border rounded-lg p-3 flex items-center gap-2 justify-center transition-colors ${
                  selectedOption === opt.id ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                {getIconUrl(opt) ? (
                  <img src={getIconUrl(opt)} alt={opt.label} className="h-6 object-contain" />
                ) : (
                  <div className="w-12 h-8 bg-gradient-to-r from-blue-500 to-green-500 rounded flex items-center justify-center text-white font-bold text-xs">
                    {opt.badge}
                  </div>
                )}
                <span className="text-sm font-semibold text-gray-900">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Payment Details */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          {current.type === 'upi' ? (
            <div className="border-2 border-dashed border-green-500 rounded-lg p-4">
              <div className="bg-white rounded-lg shadow-sm p-6 text-center relative">
              <div className="absolute top-2 right-2 flex flex-col gap-2">
                <button className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <Expand className="w-4 h-4" />
                </button>
                <button className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <Share className="w-4 h-4" />
                </button>
                <button 
                  onClick={downloadQRCode}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                  disabled={!qrCodeDataUrl}
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
                <div className="mb-4">
                  {getIconUrl(current) ? (
                    <img src={getIconUrl(current)} alt={current.label} className="h-8 mx-auto mb-2 object-contain" />
                  ) : (
                    <div className="w-12 h-8 bg-gradient-to-r from-blue-500 to-green-500 rounded flex items-center justify-center text-white font-bold text-sm mx-auto mb-2">
                      {current.badge}
                    </div>
                  )}
                  <p className="text-sm text-gray-600">Scan & Pay with Any UPI App</p>
                </div>

                {current.upiId ? (
                  <>
                    <div className="w-48 h-48 mx-auto mb-4 flex items-center justify-center">
                      {qrCodeDataUrl ? (
                        <img src={qrCodeDataUrl} alt="QR Code" className="w-full h-full object-contain" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-sm">
                          Generating QR...
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {parseFloat(amount) <= 2000 ? (
                        <p className="text-green-600 font-medium">✓ Amount included in QR</p>
                      ) : (
                        <p className="text-orange-600 font-medium">Please enter ₹{Number(amount).toLocaleString('en-IN')} manually in your UPI app</p>
                      )}
                      <div className="flex items-center justify-center gap-2 mt-2">
                        <div className="w-6 h-4 bg-blue-600 rounded text-white text-xs flex items-center justify-center">BHIM</div>
                        <div className="w-6 h-4 bg-orange-500 rounded text-white text-xs flex items-center justify-center">UPI</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-6 text-center">
                    <div className="text-4xl mb-2">📱</div>
                    <p className="text-sm text-orange-600 font-medium bg-orange-50 rounded-xl p-3">
                      UPI ID not configured yet. Please contact support or try another method.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-2">
                {current.upiId && (
                  <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-semibold">UPI ID</p>
                      <p className="text-sm font-bold text-gray-800">{current.upiId}</p>
                    </div>
                    <button onClick={() => copyToClipboard(current.upiId)} className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1">
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                )}
                {current.accountHolder && (
                  <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-semibold">Account Holder</p>
                      <p className="text-sm font-bold text-gray-800">{current.accountHolder}</p>
                    </div>
                    <button onClick={() => copyToClipboard(current.accountHolder)} className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1">
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🏦</span>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{current.bankName || 'Bank Transfer'}</h3>
                  <p className="text-xs text-gray-500">NEFT / IMPS / RTGS</p>
                </div>
              </div>
              {(current.accountHolder || current.accountNumber || current.ifsc || current.bankName) ? (
                <div className="space-y-2">
                  {[
                    { label: 'Account Holder', value: current.accountHolder },
                    { label: 'Account Number', value: current.accountNumber },
                    { label: 'IFSC Code', value: current.ifsc },
                    { label: 'Bank Name', value: current.bankName },
                  ].filter(item => item.value).map(item => (
                    <div key={item.label} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-semibold">{item.label}</p>
                        <p className="text-sm font-bold text-gray-800">{item.value}</p>
                      </div>
                      <button onClick={() => copyToClipboard(item.value)} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-orange-600 font-medium bg-orange-50 rounded-xl p-3">
                    Bank details not configured yet. Please contact support or try another method.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Upload Payment Receipt <span className="text-red-500">*</span></h2>
          <div
            className="border-2 border-dashed border-green-500 rounded-lg p-4 sm:p-6 text-center cursor-pointer relative"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {previewUrl ? (
              <div className="space-y-3">
                <img src={previewUrl} alt="receipt preview" className="mx-auto max-h-60 rounded" />
                <div className="flex items-center justify-center gap-3">
                  <Button type="button" className="h-9 bg-gray-100 text-gray-700 hover:bg-gray-200" onClick={clearReceipt}>Remove</Button>
                  <Button type="button" className="h-9" onClick={() => fileInputRef.current && fileInputRef.current.click()}>Replace</Button>
                </div>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  <span className="underline">Upload</span> or Drop a File Right Here
                </p>
                <p className="text-xs text-gray-500 mt-1">Accepted: JPG, PNG, WEBP</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          {receiptFile && (
            <div className="mt-2 text-xs text-gray-600 text-center">
              Selected: {receiptFile.name}
            </div>
          )}
        </div>

        {/* UTR Number */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Enter UTR Number <span className="text-red-500">*</span>
          </h2>
          <Input
            placeholder={extractingUtr ? "Reading from image... or type here" : "Enter UTR Number"}
            value={utrNumber}
            onChange={(e) => {
              setUtrNumber(e.target.value);
              setUtrExtractError(false);
            }}
            className="h-12"
            required
          />
          {extractingUtr && (
            <p className="text-sm text-blue-500 mt-2 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Reading UTR from image… you can also type it manually
            </p>
          )}
          {utrExtractError && !extractingUtr && (
            <p className="text-sm text-amber-600 mt-2">Could not detect UTR in the image. Please enter it manually.</p>
          )}
        </div>

        {/* Min/Max deposit info from selected method */}
        {(() => {
          const selMethod = paymentOptions.find(p => p.id === selectedOption);
          const mn = selMethod?.minAmount || 100;
          const mx = selMethod?.maxAmount || 500000;
          if (!mn && !mx) return null;
          return (
            <div className="flex items-center justify-between bg-blue-50 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-blue-500 text-sm font-medium">Min Deposit:</span>
                <span className="text-blue-700 font-bold">₹{mn.toLocaleString('en-IN')}</span>
              </div>
              <div className="w-px h-4 bg-blue-200" />
              <div className="flex items-center gap-2">
                <span className="text-blue-500 text-sm font-medium">Max Deposit:</span>
                <span className="text-blue-700 font-bold">₹{mx.toLocaleString('en-IN')}</span>
              </div>
            </div>
          );
        })()}

        {/* Terms */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-gray-700">
              I have read and agree with the{' '}
              <span className="underline">terms of payment</span> and{' '}
              <span className="underline">withdrawal policy</span>
            </span>
          </label>
        </div>

        {/* Proceed Button */}
        {(() => {
          const isValidUTR = utrNumber && utrNumber.trim().length >= 10;
          const canSubmit = isValidUTR && agreeTerms && receiptFile && !submitting;
          const missingCount = (!receiptFile ? 1 : 0) + (!isValidUTR ? 1 : 0) + (!agreeTerms ? 1 : 0);
          return (
            <>
              <Button
                onClick={handleProceed}
                disabled={!canSubmit}
                className="w-full bg-green-600 hover:bg-green-700 text-white h-12"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Proceed'
                )}
              </Button>
              {!canSubmit && !submitting && (
                <div className="text-xs mt-2 space-y-1 bg-red-50 border border-red-100 rounded-lg p-3">
                  <p className="text-gray-500 font-semibold mb-1">Complete these to proceed:</p>
                  {!receiptFile && <p className="text-red-500 font-medium">&#x274C; Upload payment receipt</p>}
                  {!utrNumber && <p className="text-red-500 font-medium">&#x274C; Enter UTR / Transaction ID</p>}
                  {utrNumber && utrNumber.trim().length < 10 && <p className="text-red-500 font-medium">&#x274C; UTR must be at least 10 characters</p>}
                  {!agreeTerms && <p className="text-red-500 font-medium">&#x274C; Accept terms &amp; conditions</p>}
                </div>
              )}
              {canSubmit && !submitting && (
                <p className="text-xs text-green-600 font-semibold mt-2 text-center">&#x2705; Ready to submit!</p>
              )}
            </>
          );
        })()}

        {/* Important Note */}
        <div className="text-center text-sm text-gray-600">
          <p>1. Deposit money only in the selected account to get the fastest credits and avoid possible delays.</p>
        </div>

        {/* Deposit History */}
        <DepositHistory />
      </div>

      {/* Pending Modal */}
      {showPendingModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl">
            <div className="text-5xl mb-4">&#9203;</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Deposit Pending</h2>
            <p className="text-gray-500 text-sm mb-4">
              {showPendingModal.message || 'You already have a pending deposit request. Please wait for it to be approved before making a new deposit request.'}
            </p>
            <div className="space-y-2">
              <button
                onClick={() => { setShowPendingModal(null); navigate('/wallet'); }}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors"
              >
                View My Requests
              </button>
              <button
                onClick={() => setShowPendingModal(null)}
                className="w-full py-2 text-gray-400 text-sm hover:text-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DepositHistory = () => {
  const [txs, setTxs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('reddyannabook_token');
        const res = await fetch(`/api/wallet/transactions?type=deposit`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setTxs((data.data || []).filter(t => t.type === 'deposit' || t.type === 'coins_added_by_master' || t.type === 'bonus').slice(0, 15));
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-center py-4 text-gray-400 text-sm">Loading history...</div>;
  if (txs.length === 0) return null;

  const fmtDate = (d) => { if (!d) return ''; try { const s = String(d).trim(); const utc = (s.includes('T')||s.includes('Z')||s.includes('+')) ? s : s.replace(' ','T')+'Z'; return new Date(utc).toLocaleDateString('en-IN', { timeZone:'Asia/Kolkata', day:'numeric', month:'short' }); } catch { return ''; } };
  const badge = (s) => {
    if (s === 'completed') return <span className="text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✅ Approved</span>;
    if (s === 'pending') return <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">⏳ Pending</span>;
    return <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">❌ Rejected</span>;
  };

  return (
    <div className="mt-6">
      <h3 className="text-sm font-bold text-gray-700 mb-2">Deposit History</h3>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-4 gap-0 px-3 py-2 bg-gray-50 text-[10px] font-bold text-gray-500 uppercase border-b">
          <div>Date</div><div>Amount</div><div>Method</div><div>Status</div>
        </div>
        {txs.map(tx => {
          const isMasterCredit = tx.type === 'coins_added_by_master';
          const isBonus = tx.type === 'bonus';
          const method = isMasterCredit ? '👑 Master Credit' : isBonus ? '🎁 Bonus' : (tx.method || 'Bank');
          return (
            <div key={`${tx.type}-${tx.id}`} className="grid grid-cols-4 gap-0 px-3 py-2 border-b border-gray-50 text-xs">
              <div className="text-gray-600">{fmtDate(tx.created_at)}</div>
              <div className={`font-bold ${(isMasterCredit || isBonus) ? 'text-green-700' : 'text-gray-900'}`}>₹{Math.abs(parseFloat(tx.amount)).toLocaleString('en-IN')}</div>
              <div className="text-gray-600 capitalize">{method}</div>
              <div>{badge(tx.status)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WalletDepositPage;
